import { ResultsView } from '@kijiji/generated/graphql-types'
import { type FC, type ReactNode, useEffect, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import { SrpHeaderSearchTitle } from '@/components/srp/header/styled'
import { SrpResults } from '@/components/srp/results-sort/results'
import { SrpSort } from '@/components/srp/results-sort/sort'
import { SearchFloatingButtonsMapSrp } from '@/components/srp/search-floating-buttons'
import { DrawerDraggableHeaderContent } from '@/components/srp/search-list/styled'
import { useMediaQuery } from '@/hooks/client-only/useMediaQuery'
import { useListingsDrawerState } from '@/hooks/srp/useListingsDrawerState'
import { DrawerDraggable } from '@/ui/atoms/drawer-draggable'
import { Flex } from '@/ui/atoms/flex'
import { SelectVariant } from '@/ui/molecules/select'

const PARTIALLY_OPENED = 0.15
const FULLY_OPENED = 1
const drawerSnapPoints = [PARTIALLY_OPENED, FULLY_OPENED]

export type ListingsDrawerProps = {
  /**
   * Listings to display inside of drawer
   */
  children: ReactNode
  searchTitle?: string
  viewType?: ResultsView
}

export const ListingsDrawer: FC<ListingsDrawerProps> = ({ children, searchTitle, viewType }) => {
  const { colors, spacing } = useTheme()
  /**
   * This is intentionally used to avoid the drawer to be in the DOM on large screens.
   * Always prefer to use CSS instead of JS for small components and Media query adjustments
   */
  const { isLargeScreen } = useMediaQuery()

  const { isListingsDrawerOpen, setListingsDrawerState } = useListingsDrawerState()
  const [activeSnapPoint, setActiveSnapPoint] = useState<string | number | null>(PARTIALLY_OPENED)

  const drawerRef = useRef<HTMLDivElement>(null)
  const isMapView = viewType === ResultsView.Map

  useEffect(() => {
    if (activeSnapPoint === PARTIALLY_OPENED && isListingsDrawerOpen) {
      drawerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [activeSnapPoint, isListingsDrawerOpen])

  useEffect(() => {
    if (isMapView && !isLargeScreen) {
      setListingsDrawerState({ isOpen: true })
    } else {
      setListingsDrawerState({ isOpen: false })
    }
  }, [isLargeScreen, isMapView, setListingsDrawerState])

  if (!isMapView || isLargeScreen) return null

  const handleCloseModal = () => {
    setActiveSnapPoint(PARTIALLY_OPENED)
  }

  const handleSetActiveSnapPoint = (snapPoint: string | number | null) => {
    setActiveSnapPoint(snapPoint)
  }

  return (
    <DrawerDraggable
      ref={drawerRef}
      snapPoints={drawerSnapPoints}
      hasBackdrop
      setActiveSnapPoint={handleSetActiveSnapPoint}
      isOpen={isListingsDrawerOpen}
      isContentScrollable={activeSnapPoint === FULLY_OPENED && isListingsDrawerOpen}
      /** It won't scroll the content behind the drawer when it is fully opened*/
      modal={activeSnapPoint === FULLY_OPENED}
      onCancel={handleCloseModal}
    >
      <SearchFloatingButtonsMapSrp isPartiallyOpen={activeSnapPoint === PARTIALLY_OPENED} />

      <Flex gap={spacing.defaultSmall} flexDirection="column">
        <DrawerDraggableHeaderContent alignItems="center" flexDirection="column">
          <SrpHeaderSearchTitle
            aria-live="polite"
            color={colors.grey.primary}
            data-testid="srp-header-title"
            size="medium"
            style={{ textAlign: 'center', alignSelf: 'center' }}
            isMapView={viewType === ResultsView.Map}
          >
            {searchTitle}
          </SrpHeaderSearchTitle>

          <SrpResults />
        </DrawerDraggableHeaderContent>

        <SrpSort variant={SelectVariant.ROUNDED} />

        {children}
      </Flex>
    </DrawerDraggable>
  )
}
